import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard,
  Users,
  AlertTriangle,
  Brain,
  FileText,
  CheckSquare,
  BarChart3,
  FileBarChart,
  LogOut,
  Menu,
  X,
  Bell,
  Trophy,
  TrendingDown,
  UserMinus,
  Activity,
  UserCog,
  CreditCard,
  Plug,
  Sparkles,
  BookOpen,
  ExternalLink,
  FileKey,
  Cloud,
  Building2,
  Crown,
  Target,
  FileSignature,
  GraduationCap,
  HelpCircle,
  Mail,
  Video,
  Shield,
  ChevronDown,
  ChevronRight,
  Settings
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import NotificationBell from './NotificationBell';
import CompanySwitcher from './CompanySwitcher';
import MasterOnboarding from './MasterOnboarding';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, company, logout, isMaster } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [alertsCount, setAlertsCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Collapsible menu state
  const [expandedMenus, setExpandedMenus] = useState({
    empresa: false,
    configuracoes: false
  });

  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAlertsCount();
    const interval = setInterval(fetchAlertsCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlertsCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/alerts/count`);
      setAlertsCount(response.data.count);
    } catch (error) {
      console.error('Error fetching alerts count:', error);
    }
  };

  const menuItems = [
    // === PRINCIPAL ===
    { path: '/superadmin/dashboard', icon: Shield, label: 'Painel Global', superadminOnly: true },
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', adminOnly: true },
    { path: '/alerts', icon: Bell, label: 'Alertas', badge: alertsCount, adminOnly: true },
    { path: '/employees', icon: Users, label: 'Funcionários', adminOnly: true },
    { path: '/risks', icon: AlertTriangle, label: 'Riscos', adminOnly: true },
    { path: '/assessments', icon: Brain, label: 'Avaliações', managerAndAbove: true },
    { path: '/trainings', icon: GraduationCap, label: 'Treinamentos', adminOnly: true },
    { path: '/dds', icon: Video, label: 'DDS', adminOnly: true },
    { path: '/ranking', icon: Trophy, label: 'Ranking', managerAndAbove: true },
    { path: '/pgr', icon: FileText, label: 'PGR', adminOnly: true },
    { path: '/aep', icon: Target, label: 'AEP (Ergonomia)', adminOnly: true },
    { path: '/action-plans', icon: CheckSquare, label: 'Planos de Ação', managerAndAbove: true },
    { path: '/reports', icon: FileBarChart, label: 'Relatórios', adminOnly: true },
    { path: '/analytics', icon: BarChart3, label: 'Analytics', adminOnly: true },
    { path: '/analise-ia', icon: Sparkles, label: 'Análise IA', adminOnly: true },
    { path: '/turnover', icon: TrendingDown, label: 'Turnover', adminOnly: true },
    { path: '/offboarding', icon: UserMinus, label: 'Offboarding', adminOnly: true },
    { path: '/absences', icon: Activity, label: 'Afastamentos', adminOnly: true },
    { 
      path: 'https://www.gov.br/trabalho-e-emprego/pt-br/assuntos/inspecao-do-trabalho/manuais-e-publicacoes/manual_gro_pgr_da_nr_1.pdf/', 
      icon: BookOpen, 
      label: 'Manual NR-1',
      external: true,
      adminOnly: true
    },
    { path: '/master', icon: Crown, label: 'Master Dashboard', masterOnly: true },
    
    // === PERFIL EMPRESA (grupo colapsável) ===
    { 
      key: 'empresa',
      icon: Building2, 
      label: 'Perfil Empresa', 
      adminOnly: true,
      isGroup: true,
      children: [
        { path: '/empresa', icon: Building2, label: 'Dados da Empresa' },
        { path: '/meu-contrato', icon: FileSignature, label: 'Meu Contrato' },
        { path: '/assinatura', icon: CreditCard, label: 'Assinatura' }
      ]
    },
    
    // === CONFIGURAÇÕES (grupo colapsável) ===
    { 
      key: 'configuracoes',
      icon: Settings, 
      label: 'Configurações', 
      adminOnly: true,
      isGroup: true,
      children: [
        { path: '/users', icon: UserCog, label: 'Usuários' },
        { path: '/integracoes', icon: Plug, label: 'Integrações' },
        { path: '/esocial', icon: FileKey, label: 'e-Social' },
        { path: '/arquivos', icon: Cloud, label: 'Arquivos Gerados' },
        { path: '/logs-notificacoes', icon: Mail, label: 'Logs de Envios' },
        { path: '/denuncias', icon: Shield, label: 'Canal de Denúncias' }
      ]
    },
    
    { path: '/ajuda', icon: HelpCircle, label: 'Central de Ajuda' }
  ];

  // Items for employees
  const employeeMenuItems = [
    { path: '/meus-treinamentos', icon: GraduationCap, label: 'Meus Treinamentos' },
    { path: '/meus-dds', icon: Video, label: 'Meus DDS' },
    { path: '/minhas-avaliacoes', icon: Brain, label: 'Minhas Avaliações' },
    { path: '/meu-perfil', icon: Users, label: 'Meu Perfil' },
    { path: '/ajuda', icon: HelpCircle, label: 'Central de Ajuda' }
  ];

  const getPageTitle = () => {
    const allItems = [...menuItems, ...employeeMenuItems];
    const currentItem = allItems.find((item) => item.path === location.pathname);
    return currentItem?.label || 'Dashboard';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check if user is an employee (not admin or manager)
  const isEmployee = user?.role === 'funcionario' || user?.role === 'employee';
  const isSuperadmin = user?.role === 'superadmin';
  
  // Filter menu items based on role
  const filteredMenuItems = isEmployee 
    ? employeeMenuItems 
    : menuItems.filter(item => {
        if (item.superadminOnly && !isSuperadmin) return false;
        if (item.adminOnly && !['admin', 'superadmin'].includes(user?.role)) return false;
        if (item.masterOnly && !isMaster) return false;
        if (item.managerAndAbove && !['admin', 'manager', 'gestor', 'superadmin'].includes(user?.role)) return false;
        return true;
      });

  return (
    <div className="flex h-screen bg-gradient-to-br from-[#e8f4fc] to-white">
      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64 translate-x-0' : isMobile ? '-translate-x-full w-64' : 'w-20'
        } ${isMobile ? 'fixed z-50 h-full' : ''} bg-white border-r border-slate-100 transition-all duration-300 flex flex-col shadow-sm`}
        data-testid="sidebar"
      >
        {/* Logo Area */}
        <div className="p-4 flex items-center justify-between border-b border-slate-100">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#1e3a5f] to-[#0d9488] rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-sm">C</span>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold tracking-tight text-[#1e3a5f]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  CERP
                </span>
                <span className="text-xs font-semibold text-[#0d9488] -mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  BRASIL NR1
                </span>
              </div>
            </div>
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-[#1e3a5f] to-[#0d9488] rounded-xl flex items-center justify-center mx-auto">
              <span className="text-white font-bold text-sm">C</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-[#e8f4fc] rounded-lg transition-colors text-[#64748b] hover:text-[#1e3a5f]"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto" data-testid="navigation">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = !item.external && !item.isGroup && location.pathname === item.path;
            
            // Group with children (collapsible)
            if (item.isGroup) {
              const isExpanded = expandedMenus[item.key];
              const hasActiveChild = item.children?.some(child => location.pathname === child.path);
              
              return (
                <div key={item.key} className="mb-1">
                  {/* Group header */}
                  <button
                    onClick={() => toggleMenu(item.key)}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      hasActiveChild
                        ? 'bg-[#e8f4fc] text-[#0d9488]'
                        : 'text-[#64748b] hover:bg-[#e8f4fc] hover:text-[#1e3a5f]'
                    }`}
                    data-testid={`nav-group-${item.key}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={20} className={hasActiveChild ? 'text-[#0d9488]' : ''} />
                      {sidebarOpen && (
                        <span className="text-sm font-medium">{item.label}</span>
                      )}
                    </div>
                    {sidebarOpen && (
                      isExpanded 
                        ? <ChevronDown size={16} className="text-[#94a3b8]" />
                        : <ChevronRight size={16} className="text-[#94a3b8]" />
                    )}
                  </button>
                  
                  {/* Children */}
                  {sidebarOpen && isExpanded && (
                    <div className="ml-4 mt-1 border-l-2 border-slate-200 pl-2">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const isChildActive = location.pathname === child.path;
                        
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`flex items-center gap-3 px-4 py-2 mb-1 rounded-lg transition-all duration-200 ${
                              isChildActive
                                ? 'bg-[#e8f4fc] text-[#0d9488]'
                                : 'text-[#64748b] hover:bg-[#e8f4fc] hover:text-[#1e3a5f]'
                            }`}
                            data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <ChildIcon size={18} className={isChildActive ? 'text-[#0d9488]' : ''} />
                            <span className="text-sm">{child.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            // External link
            if (item.external) {
              return (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 px-4 py-3 mb-1 rounded-xl transition-all duration-200 text-[#64748b] hover:bg-[#e8f4fc] hover:text-[#1e3a5f]"
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={20} />
                    {sidebarOpen && (
                      <>
                        <span className="text-sm font-medium">{item.label}</span>
                        <ExternalLink size={14} className="text-[#94a3b8]" />
                      </>
                    )}
                  </div>
                </a>
              );
            }
            
            // Regular link
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center justify-between gap-3 px-4 py-3 mb-1 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-[#e8f4fc] text-[#0d9488] border-l-[3px] border-[#0d9488]'
                    : 'text-[#64748b] hover:bg-[#e8f4fc] hover:text-[#1e3a5f]'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <Icon size={20} className={isActive ? 'text-[#0d9488]' : ''} />
                  {sidebarOpen && (
                    <span className="text-sm font-medium">{item.label}</span>
                  )}
                </div>
                {sidebarOpen && item.badge > 0 && (
                  <Badge className="bg-[#ef4444] text-white text-xs px-2 py-0.5">
                    {item.badge}
                  </Badge>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-100">
          {sidebarOpen && (
            <div className="mb-3 px-2">
              <p className="text-xs text-[#64748b] mb-1">{company?.nome}</p>
              <p className="text-sm font-medium text-[#1e3a5f]">{user?.nome}</p>
              {user?.role === 'superadmin' ? (
                <Badge className="mt-1 text-xs bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-sm">
                  <Shield className="w-3 h-3 mr-1" />
                  Superadmin
                </Badge>
              ) : user?.role === 'admin' ? (
                <Badge className="mt-1 text-xs bg-[#0d9488] text-white">
                  Admin
                </Badge>
              ) : (
                <Badge className="mt-1 text-xs bg-slate-500 text-white">
                  {user?.role}
                </Badge>
              )}
            </div>
          )}
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-[#64748b] hover:bg-[#e8f4fc] hover:text-[#1e3a5f] rounded-xl"
            data-testid="logout-button"
          >
            <LogOut size={20} className="mr-2" />
            {sidebarOpen && 'Sair'}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-slate-100 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button */}
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-[#e8f4fc] rounded-xl transition-colors text-[#64748b] hover:text-[#1e3a5f] lg:hidden"
                data-testid="mobile-menu-toggle"
              >
                <Menu size={24} />
              </button>
            )}
            <h2 
              className="text-xl md:text-2xl font-bold text-[#1e3a5f]" 
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="page-title"
            >
              {getPageTitle()}
            </h2>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {/* Company Switcher for Master Plan */}
            {isMaster && <CompanySwitcher />}
            <NotificationBell />
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 md:p-8" data-testid="main-content">{children}</div>
        
        {/* Footer */}
        <footer className="border-t border-slate-100 bg-white/50 py-4 px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-sm text-slate-500">
            <p>&copy; {new Date().getFullYear()} Ogunhê Desenvolvimento de Sistemas Ltda. Todos os direitos reservados.</p>
            <div className="flex items-center gap-4">
              <Link to="/termos-de-uso" target="_blank" className="hover:text-teal-600 transition-colors">
                Termos de Uso
              </Link>
              <Link to="/politica-de-privacidade" target="_blank" className="hover:text-teal-600 transition-colors">
                Política de Privacidade
              </Link>
            </div>
          </div>
        </footer>
      </main>
      
      {/* Master Onboarding Tour */}
      <MasterOnboarding />
    </div>
  );
};

export default Layout;
